import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { ERROR_CODES, getApiErrorCode } from '@ewentscompany/ewents-settings';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, filter, switchMap } from 'rxjs/operators';
import { LoginService } from '../../auth/login.service';
import { buildEndpointUrl, SERVICES, shouldInterceptEndpoint } from '../constants/api.constants';
import { DeviceInfoService } from '../services/device-info.service';

/**
 * Auth challenge endpoints validate credentials / TOTP / OAuth — not an existing
 * session. A 401/403 here means "login failed", so the interceptor must NOT treat
 * it as a revoked session and force a global logout. Genuine session problems
 * still surface as 401 on every other endpoint and log the user out.
 */
const AUTH_CHALLENGE_PATHS = ['/2fa/verify', '/login/'];

function isAuthChallengeEndpoint(url: string): boolean {
  return AUTH_CHALLENGE_PATHS.some(path => url.includes(path));
}

/** The IdP ended the session (contract: docs/auth-sessions.md). Nothing else logs out. */
const SESSION_ENDED_401 = new Set<string>([
  ERROR_CODES.NO_AUTH_HEADER,
  ERROR_CODES.NO_TOKEN,
  ERROR_CODES.INVALID_TOKEN,
  ERROR_CODES.INVALID_SESSION,
]);

function isSessionRejection(error: HttpErrorResponse): boolean {
  const code = getApiErrorCode(error);
  if (!code) return false;
  if (error.status === 401) return SESSION_ENDED_401.has(code);
  return error.status === 403 && code === ERROR_CODES.INACTIVE_USER;
}

function isSessionSuperseded(error: HttpErrorResponse): boolean {
  return error.status === 409 && getApiErrorCode(error) === ERROR_CODES.SESSION_SUPERSEDED;
}

export const AuthInterceptor: HttpInterceptorFn = (req, next): Observable<any> => {
  const loginService = inject(LoginService);
  const deviceInfoService = inject(DeviceInfoService);

  if (!shouldInterceptEndpoint(req.url)) {
    return next(req);
  }

  return from(deviceInfoService.getSessionHeaders()).pipe(
    switchMap(headers => {
      const sessionEpoch = loginService.getSessionEpoch();
      const request = req.clone({
        // Same-origin GETs carry no Origin: declare the product so the IdP never guesses.
        setHeaders: { ...headers, 'X-Ewents-Session-Product': 'portal' },
        withCredentials: true,
      });

      const canHandleSession = () =>
        sessionEpoch === loginService.getSessionEpoch() && !isAuthChallengeEndpoint(request.url);
      const fail = (error: HttpErrorResponse, afterRetry = false) => {
        if (
          canHandleSession() &&
          (isSessionRejection(error) || (afterRetry && isSessionSuperseded(error))) &&
          loginService.isAuthenticated()
        ) {
          loginService.setLoggedOut();
        }
        return throwError(() => error);
      };

      return next(request).pipe(
        catchError((error: HttpErrorResponse) => {
          if (!canHandleSession() || !isSessionSuperseded(error)) return fail(error);
          // A late response may refer to a cookie already replaced by another request.
          // Retry reads once with the browser's current cookie. Never replay a write;
          // probe /me instead so a retired cookie cannot strand the UI indefinitely.
          const isRead = request.method === 'GET' || request.method === 'HEAD';
          const retry = isRead
            ? request
            : new HttpRequest('GET', buildEndpointUrl(SERVICES.USERS, '/me'), {
                headers: request.headers,
                withCredentials: true,
              });
          return next(retry).pipe(
            catchError((retryError: HttpErrorResponse) => fail(retryError, true)),
            filter(event => event instanceof HttpResponse),
            switchMap(response => (isRead ? of(response) : throwError(() => error)))
          );
        })
      );
    })
  );
};
